/**
 * Pure helpers + tuning knobs for the Restore flow. No JSX, no side effects,
 * nothing time- or random-based at module scope — everything is deterministic
 * per input (design DNA, same as timeline/helpers.ts).
 */
import type { Item, Sweep } from '@vault/shared';
import type { BrowserName } from '@vault/ui';
import { hostname, initial } from '../_vault/shared.js';

// ── flow input contract ─────────────────────────────────────────────────────
export interface RestoreItem {
  url: string;
  title: string;
}
export interface RestoreSession {
  title: string | null;
  items: RestoreItem[];
}

/** Where the reopened tabs should land. */
export type RestoreTarget = 'new' | 'current';

// ── tuning knobs ────────────────────────────────────────────────────────────
/** ≤ this many tabs: open everything inside the confirm click (single user
 *  gesture — friendliest to pop-up blockers). Above it we batch + throttle. */
export const BATCH_THRESHOLD = 8;
/** Tabs opened per throttled batch. */
export const BATCH_SIZE = 4;
/** Pause between batches so the browser doesn't choke. */
export const BATCH_DELAY_MS = 260;
/** Sessions larger than this get an extra "big session" warning in confirm. */
export const SAFETY_CAP = 15;
/** How long the Undo toast sticks around (matches the "9S" in the design). */
export const UNDO_SECONDS = 9;
/** Feature string for the first tab in "new window" mode — a features-bearing
 *  window.open is the only way a web page can force a separate window. */
export const NEW_WINDOW_FEATURES = 'width=1440,height=900,left=80,top=60';

// ── mapping from vault API types ────────────────────────────────────────────
/** Convenience: build the flow input straight from a Sweep + its Items. */
export function sessionFromSweep(sweep: Sweep, items: Item[]): RestoreSession {
  return {
    title: sweep.title,
    items: items.map((i) => ({ url: i.url, title: i.title })),
  };
}

// ── environment ─────────────────────────────────────────────────────────────
/** The browser this web app is running in — the only place tabs can open. */
export function detectBrowser(): BrowserName {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/YaBrowser\//i.test(ua)) return 'yandex';
  if (/Edg(e|A|iOS)?\//i.test(ua)) return 'edge';
  if (/Firefox\//i.test(ua)) return 'firefox';
  if (/Comet\//i.test(ua)) return 'comet';
  if (/Chrome\//i.test(ua)) return 'chrome';
  if (/Safari\//i.test(ua)) return 'safari';
  return 'chrome';
}

// ── formatting ──────────────────────────────────────────────────────────────
export function browserTitle(b: BrowserName): string {
  return b.charAt(0).toUpperCase() + b.slice(1);
}

export function tabsWord(n: number): string {
  return n === 1 ? 'tab' : 'tabs';
}

/** Site-initial letters for the confirm header's favicon stack (never real
 *  logos — design DNA), plus the "+N" overflow count. */
export function stackLetters(
  items: readonly RestoreItem[],
  max = 2,
): { letters: string[]; overflow: number } {
  const letters = items.slice(0, max).map((it) => initial(hostname(it.url)));
  return { letters, overflow: Math.max(0, items.length - max) };
}
